import React from "react";
import {
  Box,
  Grid,
  Typography,
  Container,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion } from "framer-motion";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import StarIcon from "@mui/icons-material/Star";

const ServicesComparisonView = ({
  title,
  standardServices,
  premiumServices,
  standardPrice,
  premiumPrice,
  standardImage, // Image for Standard
  premiumImage,  // Image for Premium
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        backgroundColor: "#f9f9f9",
        paddingBottom: { xs: "2rem", md: "3rem" },
      }}
    >
      {/* Hero Section with Solid Animation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <Box
          sx={{
            textAlign: "center",
            padding: { xs: "1rem 0", md: "2rem 0" },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "2rem", md: "2.5rem" },
              mb: 1,
              color: "black",
            }}
          >
            Periodic Car Services Comparison
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: { xs: "1rem", md: "1.3rem" }, color: "gray" }}
          >
            Compare the benefits of our Standard and Premium services.
          </Typography>
        </Box>
      </motion.div>

      {/* Service Comparison Section */}
      <Container
        sx={{  textAlign: "center" }}
      >
        {!isMobile ? (
          <Grid container spacing={4}>
            {/* Standard Services with Slide In Animation */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: -100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    padding: { xs: "1.5rem", md: "2rem" },
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Standard Service Image */}
                  <Box
                    component="img"
                    src={standardImage}
                    alt="Standard Service"
                    sx={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                      objectFit: "cover",
                    }}
                  />
                  
                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "orangered", mb: 2 }}
                  >
                    Standard Services
                  </Typography>
                  <motion.div
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#333",
                        mb: 4,
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                      }}
                    >
                      Price starts from: &#8377;{standardPrice}
                    </Typography>
                  </motion.div>
                  {standardServices.map((service, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mb: 2 }}
                    >
                      <CheckCircleOutlineIcon
                        sx={{ color: "orangered", mr: 2 }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, textAlign: "left" }}
                      >
                        {service}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>

            {/* Premium Services with Slide In and Glow Animation */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ x: 100, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    padding: { xs: "1.5rem", md: "2rem" },
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                >
                  {/* Premium Service Image */}
                  <Box
                    component="img"
                    src={premiumImage}
                    alt="Premium Service"
                    sx={{
                      width: "100%",
                      borderRadius: "8px",
                      marginBottom: "1rem",
                      objectFit: "cover",
                    }}
                  />

                  <Typography
                    variant="h4"
                    sx={{ fontWeight: 700, color: "goldenrod", mb: 2 }}
                  >
                    Premium Services
                  </Typography>
                  <motion.div
                    initial={{ scale: 0.8 }}
                    whileInView={{ scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.5, ease: "easeOut" }}
                    viewport={{ once: true }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#333",
                        mb: 4,
                        fontSize: { xs: "1.2rem", md: "1.5rem" },
                      }}
                    >
                      Price starts from: &#8377;{premiumPrice}
                    </Typography>
                  </motion.div>
                  {premiumServices.map((service, index) => (
                    <Box
                      key={index}
                      sx={{ display: "flex", alignItems: "center", mb: 2 }}
                    >
                      {standardServices.includes(service) ? (
                        <CheckCircleOutlineIcon
                          sx={{ color: "goldenrod", mr: 2 }}
                        />
                      ) : (
                        <motion.div
                          initial={{ scale: 1 }}
                          animate={{ scale: 1.1 }}
                          transition={{ duration: 0.5, yoyo: Infinity }}
                          whileHover={{ scale: 1.2 }}
                        >
                          <StarIcon sx={{ color: "goldenrod", mr: 2 }} />
                        </motion.div>
                      )}
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, textAlign: "left" }}
                      >
                        {service}{" "}
                        {standardServices.includes(service) ? (
                          ""
                        ) : (
                          <span
                            style={{
                              color: "goldenrod",
                              fontSize: "0.9rem",
                              fontWeight: 600,
                            }}
                          >
                            + Extra Benefit
                          </span>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        ) : (
          // Mobile Accordion View
          <AccordionView
            standardServices={standardServices}
            premiumServices={premiumServices}
            standardPrice={standardPrice}
            premiumPrice={premiumPrice}
            standardImage={standardImage}
            premiumImage={premiumImage}
          />
        )}

        {/* Visual Divider */}
        <Divider sx={{ my: 4 }} />

        {/* Disclaimer Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <Box
            sx={{
              padding: { xs: "1.5rem", md: "1rem 2rem" },
              textAlign: "left",
              backgroundColor: "#ececec",
              borderRadius: "8px",
              marginTop: "-1rem",
              maxWidth: "800px",
              mx: "auto",
              mt: 2,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontSize: "0.85rem", lineHeight: 1.5 }}
            >
              * Specialist oil or different grades of oil may be required for your vehicle that will incur an additional cost. Labor prices may vary based on vehicle model and will be quoted on the day of service.
            </Typography>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

// Accordion View for Mobile with updated styling
const AccordionView = ({
  standardServices,
  premiumServices,
  standardPrice,
  premiumPrice,
  standardImage,
  premiumImage,
}) => (
  <>
    <Accordion
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        marginBottom: "1rem",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "orangered" }} />}
        sx={{
          padding: "0.75rem 1.5rem",
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Typography sx={{ fontWeight: "bold" }}>Standard Services</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "1rem 1.5rem" }}>
        <Box
          component="img"
          src={standardImage}
          alt="Standard Service"
          sx={{
            width: "100%",
            borderRadius: "8px",
            marginBottom: "1rem",
            objectFit: "cover",
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Price: ${standardPrice}
        </Typography>
        {standardServices.map((service, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <CheckCircleOutlineIcon sx={{ color: "orangered", mr: 2 }} />
            <Typography variant="body2">{service}</Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>

    <Accordion
      sx={{
        borderRadius: "12px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
        marginBottom: "1rem",
        "&:before": {
          display: "none",
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "orangered" }} />}
        sx={{
          padding: "0.75rem 1.5rem",
          "& .MuiAccordionSummary-content": {
            margin: 0,
          },
        }}
      >
        <Typography sx={{ fontWeight: "bold" }}>Premium Services</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: "1rem 1.5rem" }}>
        <Box
          component="img"
          src={premiumImage}
          alt="Premium Service"
          sx={{
            width: "100%",
            borderRadius: "8px",
            marginBottom: "1rem",
            objectFit: "cover",
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Price: ${premiumPrice}
        </Typography>
        {premiumServices.map((service, index) => (
          <Box key={index} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            {standardServices.includes(service) ? (
              <CheckCircleOutlineIcon sx={{ color: "goldenrod", mr: 2 }} />
            ) : (
              <StarIcon sx={{ color: "goldenrod", mr: 2 }} />
            )}
            <Typography variant="body2">{service}</Typography>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  </>
);



export default ServicesComparisonView;
